import { computed, onMounted, onBeforeUnmount, shallowRef } from 'vue'
import { resolveStickyBottom } from '@/utils/sticky-offset'
import { useFrameSampler } from '@/composables/useFrameSampler'

// Marks the "filters active" bar so a sticky element can measure it and park
// above it. Owned here rather than by either component: it is the contract
// between them, as STICKY_BRANDING_BAR_CLASS is for the top bars.
export const FILTERS_ACTIVE_BAR_CLASS = 'active-filters-notice'

const BAR_ROOT_SELECTOR = `.${FILTERS_ACTIVE_BAR_CLASS}`
// The bar's visible box. The snackbar's overlay root spans the whole page.
const BAR_SELECTOR = `${BAR_ROOT_SELECTOR} .v-snackbar__wrapper`

// The bar slides in and out over ~150ms. One sliding out is removed from the
// page before its transition ends, so no transitionend ever reports it: the
// tail outlasts the slide to see it gone.
const SETTLE_FRAMES = 20

/**
 * Drives the `bottom` of a `position: sticky` element the page scrolls, so it
 * parks above the "filters active" bar while that bar shows. The rest of the
 * time the element's own CSS offset applies, e.g. `var(--v-layout-bottom)` to
 * clear AppBottomNav. See resolveStickyBottom for why the bar is measured.
 *
 * @returns {{ offsetStyle: import('vue').ComputedRef }} inline style to bind,
 *   null while the bar is not showing
 */
export function useStickyBottom() {
  const offset = shallowRef(null)

  const offsetStyle = computed(() => (offset.value === null ? null : { bottom: `${offset.value}px` }))

  const sample = () => {
    const next = resolveStickyBottom(window.innerHeight, document.querySelector(BAR_SELECTOR)?.getBoundingClientRect())

    if (next === offset.value) return false
    offset.value = next
    return true
  }

  const { schedule } = useFrameSampler(sample, SETTLE_FRAMES)

  const onBarTransition = event => {
    if (event.target?.closest?.(BAR_ROOT_SELECTOR)) schedule()
  }

  // Wrapped: passing `schedule` straight to addEventListener would feed the Event
  // object in as the frame budget.
  const resample = () => schedule()

  onMounted(() => {
    schedule()
    window.addEventListener('resize', resample, { passive: true })
    document.addEventListener('transitionrun', onBarTransition, true)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', resample)
    document.removeEventListener('transitionrun', onBarTransition, true)
  })

  return { offsetStyle }
}
